import { Link } from "react-router-dom";
import { Gavel, ArrowRight, Flame } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";

export const aucStatus = (a) => a.status || (a.ends_at && new Date(a.ends_at) < new Date() ? "ended" : a.starts_at && new Date(a.starts_at) > new Date() ? "scheduled" : "live");
export const aucId = (a) => a.id || a._id || a.auction_id;
export const aucPrice = (a) => a.current_price ?? a.current_bid ?? a.highest_bid ?? a.start_price ?? a.price ?? 0;
export const aucBids = (a) => a.bids_count ?? a.bids ?? a.total_bids ?? 0;
export const aucEnd = (a) => a.ends_at || a.end_at || a.end || a.ends;

export const timeLeft = (end, t) => {
  if (!end) return "";
  const ms = new Date(end).getTime() - Date.now();
  if (ms <= 0) return t("auc_ended");
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
};

export function AuctionBadge({ status }) {
  const { t } = useLang();
  if (status === "live")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow">
        <Flame className="h-3 w-3" /> {t("auc_live")}
      </span>
    );
  if (status === "scheduled")
    return (
      <span className="inline-flex items-center rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-amber-950 shadow">
        {t("auc_soon")}
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full bg-slate-500 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white shadow">
      {t("auc_ended")}
    </span>
  );
}

export function AuctionCard({ a, tid }) {
  const { t } = useLang();
  const { fmt } = useCurrency();
  const status = aucStatus(a);
  const end = aucEnd(a);
  return (
    <Link
      data-testid={tid}
      to={`/auctions/${aucId(a)}`}
      className="group block w-40 shrink-0 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 transition hover:ring-teal-300"
    >
      <div className="relative aspect-square bg-teal-50">
        {a.image && (
          <img src={mediaUrl(a.image)} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        <span className="absolute left-2 top-2"><AuctionBadge status={status} /></span>
      </div>
      <div className="p-2.5">
        <p className="line-clamp-2 text-[11px] font-semibold leading-snug text-slate-700">{a.title}</p>
        <p className="mt-1 text-sm font-extrabold text-slate-900">{fmt(aucPrice(a))}</p>
        <p className="text-[10px] text-slate-400">
          {aucBids(a)} {t("auc_bids")}{end && status === "live" ? ` · ${t("auc_ends")} ${timeLeft(end, t)}` : ""}
        </p>
      </div>
    </Link>
  );
}

export default function AuctionSection({ auction }) {
  const { t } = useLang();
  if (!auction?.enabled) return null;
  const items = auction.items || [];
  return (
    <section data-testid="auction-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      {auction.show_banner && auction.banner && (auction.banner.title || auction.banner.images?.length > 0) && (
        <Link
          data-testid="auction-banner"
          to="/auctions"
          className="relative block overflow-hidden rounded-3xl bg-gradient-to-r from-amber-500 to-orange-600 p-6 text-white shadow-lg shadow-orange-600/25"
        >
          {auction.banner.images?.[0] && (
            <img src={mediaUrl(auction.banner.images[0])} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          )}
          <div className="relative flex items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-heading text-xl font-extrabold tracking-tight">
                <Gavel className="h-5 w-5" /> {auction.banner.title}
              </p>
              {auction.banner.subtitle && <p className="mt-1 text-xs text-orange-50">{auction.banner.subtitle}</p>}
              {auction.banner.promo && (
                <span className="mt-2 inline-block rounded-full bg-white px-3 py-1 text-[11px] font-extrabold text-orange-700">{auction.banner.promo}</span>
              )}
            </div>
            <ArrowRight className="h-5 w-5 shrink-0" />
          </div>
        </Link>
      )}
      {auction.show_products && items.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-base md:text-lg font-bold text-slate-900">
              <Gavel className="h-4 w-4 text-orange-600" /> {t("auc_top")}
            </h2>
            <Link data-testid="auction-view-all" to="/auctions" className="flex items-center gap-0.5 text-xs font-semibold text-slate-500 hover:text-teal-700">
              {t("view_all")} <ChevronRightMini />
            </Link>
          </div>
          <div data-testid="auction-items" className="-mx-4 mt-3 flex gap-3 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {items.map((a) => (
              <AuctionCard key={aucId(a)} a={a} tid={`auction-card-${aucId(a)}`} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const ChevronRightMini = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
);
